// -----------------------------------------------------------------------------
// "Create a channel" modal — name + optional description + initial members.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/24/solid";
import { HashtagIcon, XMarkIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import classNames from "../../../utils/classNames";
import { fullNameFor } from "../../../utils/chatHelpers";
import { IChatUser } from "../../../models/Chat";
import ChatAvatar from "../ChatAvatar";

interface NewChannelModalProps {
  open: boolean;
  onClose: () => void;
  users: IChatUser[];
  meId: string | null;
  creating: boolean;
  onCreate: (input: {
    name: string;
    description?: string;
    memberIds: string[];
  }) => void;
}

export default function NewChannelModal({
  open,
  onClose,
  users,
  meId,
  creating,
  onCreate,
}: NewChannelModalProps) {
  const { t } = useTranslation("chat");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setName("");
      setDesc("");
      setMemberIds([]);
    }
  }, [open]);

  const toggleMember = (id: string) =>
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const canCreate = name.trim().length > 0 && !creating;

  const selectableUsers = users.filter((u) => u.id !== meId);

  return (
    <Modal open={open} setOpen={(v) => !v && onClose()} width="sm:max-w-lg">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {t("Create a channel")}
          </h3>
          <p className="text-xs text-gray-500">
            {t("Channels are a place for the team to talk about a specific topic or workflow.")}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("Name")}
          </span>
          <div className="mt-1 flex overflow-hidden rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
            <span className="flex items-center bg-gray-50 px-2 text-gray-400">
              <HashtagIcon className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("e.g. vehicle-arrivals")}
              className="w-full border-0 p-2 text-sm focus:ring-0"
              autoFocus
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("Description")} <span className="font-normal">{t("(optional)")}</span>
          </span>
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={t("What's this channel for?")}
            className="mt-1 w-full rounded-md border-gray-300 p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </label>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("Members")}
          </p>
          <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200">
            {selectableUsers.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-500">
                {t("No teammates to add yet.")}
              </p>
            ) : (
              selectableUsers.map((u) => {
                const checked = memberIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleMember(u.id)}
                    className={classNames(
                      "flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50",
                      checked ? "bg-primary-50" : ""
                    )}
                  >
                    <ChatAvatar user={u} size="sm" />
                    <span className="flex-1 text-sm">
                      <span className="block font-medium text-gray-900">
                        {fullNameFor(u)}
                      </span>
                      {u.role ? (
                        <span className="block text-xs text-gray-500">
                          {u.role}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={classNames(
                        "flex h-5 w-5 items-center justify-center rounded border",
                        checked
                          ? "border-primary-600 bg-primary-600 text-white"
                          : "border-gray-300 bg-white"
                      )}
                    >
                      {checked ? <CheckIcon className="h-3 w-3" /> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          disabled={!canCreate}
          onClick={() =>
            onCreate({
              name: name.trim(),
              description: desc.trim() || undefined,
              memberIds,
            })
          }
          className={classNames(
            "rounded-md px-3 py-1.5 text-sm font-semibold text-white",
            canCreate ? "bg-primary-600 hover:bg-primary-500" : "bg-gray-300"
          )}
        >
          {creating ? t("Creating…") : t("Create channel")}
        </button>
      </div>
    </Modal>
  );
}
